import { PropTypes } from 'prop-types';
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import AccionaTableBody from './AccionaTableBody';
import AccionaTableHeader from './AccionaTableHeader';
import { useSelector } from 'react-redux';
import AccionaTablePagination from './AccionaTablePagination';
import { MainContext } from '../../context/MainContext';
import { useOutletContext } from 'react-router-dom';
import { Empty } from 'antd';
import styles from './index.module.scss';

const { header } = styles;

const AccionaTable = ({
  columns,
  getItems,
  entity,
  pageSize,
  setSelectedRows,
  selectedRows,
  showTitle,
  dataParseFunction,
  onChange,
  actions,
  initialFilters,
  allowClear,
  onClearFilters,
  allowClearStyle,
  expandable,
  isSelectionDisabled,
  title,
  rowHeight,
  hideSelectAllCheckbox,
  showSizeChanger,
  skipFetchingUntilFilter,
  actionsColumnWidth,
  filtersOverride,
}) => {
  const intl = useIntl();
  const {
    list: items,
    total,
    fetching,
  } = useSelector((store) => store[entity]);
  const { setLoading } = useContext(MainContext);
  const { project } = useOutletContext() || {};
  const [pagination, setPagination] = useState({
    current: 1,
    total,
    pageSize,
  });
  const [filters, setFilters] = useState(
    initialFilters || {
      sortable: columns.docket_number?.ascSort ? 'docket_number-ASC' : '',
    },
  );
  const pid = project?.id_project;
  const { current: currentPage, pageSize: currentPageSize } = pagination;
  const prevPage = useRef();
  const prevTotal = useRef(total);

  const onClickCheckBoxHeader = (checked) => {
    if (checked) {
      const allSelected = [];
      items.forEach((element) => {
        if (!isSelectionDisabled(element))
          allSelected.push(
            Object.assign(element, { page: pagination.current }),
          );
      });
      setSelectedRows(allSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const onClickCheckBox = (checked, object) => {
    if (checked) {
      if (selectedRows.findIndex((x) => x.id === object.id) < 0) {
        setSelectedRows([
          ...selectedRows,
          Object.assign(object, { page: pagination.current }),
        ]);
      }
    } else {
      const index = selectedRows.findIndex((item) => item.id === object.id);
      setSelectedRows([
        ...selectedRows.slice(0, index),
        ...selectedRows.slice(index + 1),
      ]);
    }
  };

  const handleSetPagination = useCallback(
    (newPagination) => setPagination(newPagination),
    [],
  );

  const handleSetFilters = useCallback((newFilters) => {
    setFilters(newFilters);

    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, []);

  useEffect(() => {
    if (
      !skipFetchingUntilFilter ||
      (skipFetchingUntilFilter && filters[skipFetchingUntilFilter])
    ) {
      getItems({
        size: currentPageSize,
        page: currentPage,
        filters: { pid, ...filters },
      });
      prevPage.current = currentPage;
    }
  }, [
    currentPage,
    currentPageSize,
    filters,
    getItems,
    pid,
    skipFetchingUntilFilter,
  ]);

  useEffect(() => {
    const totalChanged = pagination.total !== total;
    const totalDecreased = prevTotal.current > total;

    if (totalChanged) {
      setPagination((prev) => ({ ...prev, total }));

      if (totalDecreased && total > 0) {
        getItems({
          size: currentPageSize,
          page: currentPage,
          filters: { pid, ...filters },
        });
      }
    }

    prevTotal.current = total;

    if (setSelectedRows) setSelectedRows([]);
  }, [
    total,
    pagination,
    setSelectedRows,
    getItems,
    currentPageSize,
    currentPage,
    pid,
    filters,
  ]);

  useEffect(() => {
    const lastPage = Math.ceil(total / currentPageSize);
    const isLastPage = currentPage === lastPage;

    if (
      items.length < currentPageSize &&
      total > 0 &&
      items.length < total &&
      !isLastPage
    ) {
      getItems({
        size: currentPageSize,
        page: currentPage,
        filters: { pid, ...filters },
      });
    }
  }, [
    currentPage,
    currentPageSize,
    filters,
    getItems,
    items.length,
    pid,
    total,
  ]);

  useEffect(() => {
    setLoading(fetching);
  }, [fetching, setLoading]);

  useEffect(() => {
    onChange({ pagination, filters });
  }, [pagination, filters, onChange]);

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const filtersVersionRef = useRef(null);
  useEffect(() => {
    if (
      filtersOverride &&
      filtersOverride.version !== filtersVersionRef.current
    ) {
      filtersVersionRef.current = filtersOverride.version;
      setFilters(filtersOverride.filters);
    }
  }, [filtersOverride]);

  return (
    <div className="row" id="AccionaTable">
      <div className={`col-sm-12 ${header}`}>
        {showTitle && (title || intl.formatMessage({ id: entity }))}
      </div>
      <div className="col-sm-12">
        <table className="dataTable" style={{ backgroundColor: '#fff' }}>
          <AccionaTableHeader
            isSelectionDisabled={isSelectionDisabled}
            columns={columns}
            hiddenActionCol={!actions}
            hiddenCheckBoxCol={setSelectedRows ? false : true}
            hideSelectAllCheckbox={hideSelectAllCheckbox}
            onClickCheckBoxHeader={onClickCheckBoxHeader}
            selectedRows={selectedRows}
            totalItems={items.length || 0}
            selectedPage={pagination.current}
            entity={entity}
            setFilters={handleSetFilters}
            setPagination={handleSetPagination}
            total={total}
            filters={filters}
            expandable={expandable}
            items={items}
            actionsColumnWidth={actionsColumnWidth}
          />
          {items.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={
                    Object.keys(columns).length + (setSelectedRows ? 2 : 1)
                  }
                >
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </td>
              </tr>
            </tbody>
          ) : (
            <AccionaTableBody
              bodyData={dataParseFunction ? dataParseFunction(items) : items}
              columns={columns}
              onClickCheckBox={setSelectedRows ? onClickCheckBox : undefined}
              selectedRows={selectedRows}
              actions={actions}
              entity={entity}
              pageSize={currentPageSize}
              expandable={expandable}
              isSelectionDisabled={isSelectionDisabled}
              rowHeight={rowHeight}
              actionsColumnWidth={actionsColumnWidth}
            />
          )}
        </table>
        <div
          className="col-sm-12"
          style={{
            backgroundColor: '#fff',
            height: '43px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {pagination.total !== 0 && (
            <AccionaTablePagination
              pagination={pagination}
              showSizeChanger={showSizeChanger}
              setPagination={handleSetPagination}
            />
          )}
        </div>
      </div>
    </div>
  );
};

AccionaTable.propTypes = {
  columns: PropTypes.object.isRequired,
  getItems: PropTypes.func.isRequired,
  entity: PropTypes.string.isRequired,
  pageSize: PropTypes.number,
  setSelectedRows: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  showTitle: PropTypes.bool,
  dataParseFunction: PropTypes.func,
  onChange: PropTypes.func,
  actions: PropTypes.func,
  initialFilters: PropTypes.object,
  allowClear: PropTypes.bool,
  onClearFilters: PropTypes.func,
  allowClearStyle: PropTypes.object,
  expandable: PropTypes.shape({
    rowExpandable: PropTypes.func,
    expandedRowRender: PropTypes.func,
  }),
  isSelectionDisabled: PropTypes.func,
  title: PropTypes.string,
  rowHeight: PropTypes.string,
  hideSelectAllCheckbox: PropTypes.bool,
  showSizeChanger: PropTypes.bool,
  skipFetchingUntilFilter: PropTypes.string,
  actionsColumnWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filtersOverride: PropTypes.shape({
    filters: PropTypes.object,
    version: PropTypes.number,
  }),
};
AccionaTable.defaultProps = {
  pageSize: 5,
  selectedRows: [],
  showTitle: true,
  onChange: () => {},
  columns: {},
  allowClear: false,
  onClearFilters: () => {},
  allowClearStyle: {},
  isSelectionDisabled: () => {},
  dataParseFunction: undefined,
  actions: undefined,
  initialFilters: undefined,
  expandable: undefined,
  title: undefined,
  rowHeight: '94px',
  setSelectedRows: undefined,
  hideSelectAllCheckbox: false,
  showSizeChanger: false,
  skipFetchingUntilFilter: undefined,
  actionsColumnWidth: 'auto',
  filtersOverride: null,
};

export default AccionaTable;
